import { useEffect, useState } from "react";
import { api, formatDate, formatCents } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, Stat, EmptyState, Input } from "@/components/ui/Primitives";
import { Gift, Copy, Share2, Users2, Trophy, Sparkles, CheckCircle2, Hourglass } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [code, setCode] = useState("");
  const [milestone, setMilestone] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [refRes, msRes] = await Promise.all([
        api.get("/referrals/me"),
        api.get("/referrals/milestone-status").catch(() => ({ data: null })),
      ]);
      setData(refRes.data);
      setMilestone(msRes.data);
      // If a milestone is freshly due, trigger the check (idempotent — admin
      // will get an approval email; user sees the "pending" state).
      const ms = msRes.data;
      if (ms && ms.milestones_completed > ms.milestones_awarded + ms.milestones_pending) {
        api.post("/referrals/check-milestone").then((r) => {
          if (r?.data?.milestone_triggered) {
            toast.success("You hit a referral milestone! Reward pending approval.");
          }
          // Refresh milestone state silently.
          api.get("/referrals/milestone-status").then((s) => setMilestone(s.data)).catch(() => {});
        }).catch(() => {});
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load referrals");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      await api.post("/referrals/redeem", { code: code.trim() });
      toast.success("Code redeemed — credit applied!");
      setCode("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setRedeeming(false); }
  };

  if (loading) return <Loader/>;

  const referralCode = data?.code || data?.referral_code || "—";
  const codeReady = referralCode && referralCode !== "—" && /^[a-zA-Z0-9_-]{2,}$/.test(referralCode);
  // Always share the canonical company domain — never the preview/host URL.
  // Guard against non-ASCII / em-dash placeholders that trip
  // DOMException "The string did not match the expected pattern." on
  // Safari / iOS when passed to navigator.share() or the async
  // clipboard API. Only build a real link once the code is validated.
  const referralLink = codeReady
    ? `https://centralrentals.ca/?ref=${encodeURIComponent(referralCode)}`
    : "https://centralrentals.ca/";
  const history = data?.history || data?.referrals || [];
  const stats = data?.stats || {};
  const isLandlord = user?.role === "landlord";
  const rewardLabel = isLandlord ? "one month free" : "$50 credit";

  // Safe copy — handles both the async Clipboard API and the older
  // textarea+execCommand fallback. Wrapped in try/catch so a failure
  // never bubbles up as an uncaught DOMException.
  const safeCopy = async (text, label) => {
    if (!text) { toast.info(`${label} isn't ready yet`); return; }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(String(text));
        toast.success(`${label} copied`);
        return;
      }
    } catch (err) {
      console.warn("clipboard write failed", err);
    }
    // Fallback path
    try {
      const ta = document.createElement("textarea");
      ta.value = String(text);
      ta.style.position = "fixed"; ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`${label} copied`);
    } catch (err) {
      console.warn("fallback copy failed", err);
      toast.error(`${label} — long-press to copy manually`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Referrals & credits"
        subtitle={isLandlord ? "Invite landlords. Earn a free month for every successful sign-up." : "Refer friends. Get rent credits when they apply."}
      />

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <Card className="lg:col-span-2 p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg shadow-emerald-500/20 relative overflow-hidden">
          <Gift className="absolute top-4 right-4 w-24 h-24 text-white/5"/>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-200">Your referral code</div>
          <div className="mt-3 font-display text-5xl font-extrabold tracking-tight font-mono" data-testid="referral-code">{referralCode}</div>
          <p className="mt-3 text-white/75 text-sm max-w-md">Share this code or your personal link. Friends who sign up unlock {rewardLabel} for you when they complete a lease.</p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              onClick={() => safeCopy(referralCode, "Code")}
              disabled={!codeReady}
              data-testid="copy-code-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white text-green-900 hover:bg-emerald-50 font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Copy className="w-4 h-4"/> Copy code
            </button>
            <button
              onClick={() => safeCopy(referralLink, "Link")}
              disabled={!codeReady}
              data-testid="copy-link-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Share2 className="w-4 h-4"/> Copy link
            </button>
            {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
              <button
                onClick={() => {
                  if (!codeReady) { toast.info("Referral link isn't ready yet"); return; }
                  try {
                    // navigator.share can throw DOMException if the URL
                    // contains non-ASCII characters or the caller isn't
                    // a user-gesture-scoped context. .catch() handles
                    // async failures; try/catch handles sync throws.
                    const p = navigator.share({
                      title: "Join Central Rentals Canada",
                      text: `Use my code ${referralCode}`,
                      url: referralLink,
                    });
                    if (p && typeof p.catch === "function") {
                      p.catch((err) => { console.warn("share cancelled/failed", err); });
                    }
                  } catch (err) {
                    console.warn("share failed", err);
                  }
                }}
                disabled={!codeReady}
                data-testid="share-native-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Share…
              </button>
            )}
          </div>

          <div className="mt-5 pt-5 border-t border-white/10 text-xs text-white/55 break-all">{referralLink}</div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-base font-bold text-slate-900 mb-3">Got a code?</h3>
          <p className="text-sm text-slate-500 mb-4">Enter someone else&apos;s referral code to apply their welcome bonus to your account.</p>
          <form onSubmit={redeem} className="space-y-3">
            <Input label="Referral code" placeholder="ABC123" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} testid="redeem-code-input"/>
            <Button type="submit" className="w-full" disabled={redeeming || !code.trim()} data-testid="redeem-submit-button">{redeeming ? "Redeeming…" : "Redeem code"}</Button>
          </form>
        </Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total referrals" value={stats.total_referrals ?? history.length} icon={Users2}/>
        <Stat label="Successful" value={stats.successful ?? history.filter((h) => h.status === "rewarded" || h.status === "completed").length} icon={Trophy} accent="text-green-800"/>
        <Stat label="Pending" value={stats.pending ?? history.filter((h) => h.status === "pending" || h.status === "signed_up").length} icon={Sparkles} accent="text-amber-600"/>
        <Stat label="Credits earned" value={formatCents(stats.credits_earned_cents ?? 0)} accent="text-green-800"/>
      </div>

      {milestone && isLandlord && (
        <Card className="p-6 mb-6 bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-emerald-200" data-testid="milestone-card">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                <Trophy className="w-3 h-3"/> {milestone.milestone_size} referrals = 1 free month
              </div>
              <h3 className="font-display text-xl font-bold text-slate-950 mt-2">
                {milestone.successful_referrals} of {milestone.next_threshold} successful referrals
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {milestone.milestones_awarded > 0 && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold mr-2">
                    <CheckCircle2 className="w-3.5 h-3.5"/> {milestone.milestones_awarded} free month{milestone.milestones_awarded > 1 ? "s" : ""} earned
                  </span>
                )}
                {milestone.milestones_pending > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                    <Hourglass className="w-3.5 h-3.5"/> {milestone.milestones_pending} pending admin approval
                  </span>
                )}
                {milestone.milestones_awarded === 0 && milestone.milestones_pending === 0 && (
                  <>Share your code to earn your first free month.</>
                )}
              </p>
            </div>
            <div className="w-full md:w-72">
              <div className="h-3 rounded-full bg-white border border-emerald-200 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                  style={{ width: `${milestone.progress_pct || 0}%` }}
                  data-testid="milestone-progress-bar"/>
              </div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-2 text-right">
                {milestone.progress_pct}% to next free month
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Referral history</div>
        {history.length === 0 ? (
          <div className="py-12 text-center">
            <Gift className="w-12 h-12 mx-auto text-slate-300 mb-3"/>
            <h3 className="font-display text-base font-semibold text-slate-900">No referrals yet</h3>
            <p className="text-sm text-slate-500 mt-1">Share your code to start earning {rewardLabel}.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr><th className="px-5 py-3">Friend</th><th className="px-5 py-3">Joined</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Reward</th></tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={h.referral_id || i} className="border-b border-slate-100" data-testid={`referral-row-${i}`}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{h.referred_name || h.referred_email || `Friend ${i+1}`}</td>
                  <td className="px-5 py-3 text-slate-600">{h.created_at ? formatDate(h.created_at) : "—"}</td>
                  <td className="px-5 py-3"><Pill tone={h.status === "rewarded" || h.status === "completed" ? "green" : h.status === "pending" ? "amber" : "slate"}>{h.status || "pending"}</Pill></td>
                  <td className="px-5 py-3 text-right font-semibold">{h.reward_cents ? formatCents(h.reward_cents) : (h.status === "rewarded" ? "Applied" : "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
